import type { ServicePageConfig } from "../../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../../package-pricing";
import { getCityMunicipalityList } from "../../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const flytthjalpAkersbergaPage: ServicePageConfig = {
  slug: "flytthjalp-akersberga",
  serviceCategory: "flytt",
  city: "stockholm",

  meta: {
    title: "Flytthjälp Åkersberga – Hela Österåker | Ungdomskraft",
    description:
      "Boka flytthjälp i Åkersberga och Österåker: Tråsättra, Täljö, Margretelund, Svinninge och Österskär. Skåpbil, bärare, fast paketpris och RUT-avdrag.",
    h1: "Flytthjälp Åkersberga",
  },

  hero: {
    heading: "Flytthjälp i Åkersberga",
    intro:
      "Vi flyttar dig i Åkersberga och resten av Österåkers kommun – från lägenheten i Tråsättra eller centrum till villan i Svinninge, radhuset i Margretelund eller huset i Österskär. Bärare och skåpbil kommer till dörren, och paketpriset ser du redan när du bokar.",
    heroImage: "/hero-flytthjalp.png",
    heroImageAlt: "Ungdomskraft lastar skåpbil vid flytthjälp i Åkersberga",
  },

  featureCards: {
    sinceText: "Flytthjälp sedan 2014",
    storyText:
      "Ungdomskraft har flyttat hushåll i Stockholms län sedan 2014. I Österåker bär vi lika ofta från en lägenhet nära Roslagsbanan som från en villa vid vattnet i Svinninge, och vi planerar varje flytt efter hur det ser ut vid dörren.",
    bookYourText: "Boka flytt i Åkersberga",
    bookDescText:
      "Skriv in gamla och nya adressen, välj paket och en ledig tid. Priset visas innan du bekräftar.",
    vehicleTitleText: "Bil och bärare från E18 till dörren",
    vehicleDescText:
      "Vi kommer med skåpbil, filtar och spännband och kör hela vägen – du behöver inte hyra bil eller köra själv på Norrtäljevägen.",
  },

  itemsSection: {
    heading: "Det här flyttar vi ofta i Åkersberga",
    items: [
      {
        name: "Soffor & fåtöljer",
        description: "Hörnsoffor, modulsoffor och fåtöljer",
        image: "/sofa.webp",
        alt: "Soffa som bärs vid flytt i Åkersberga centrum",
      },
      {
        name: "Sängar & madrasser",
        description: "Dubbelsängar, barnsängar och madrasser",
        image: "/bed.webp",
        alt: "Säng som flyttas från lägenhet i Tråsättra, Åkersberga",
      },
      {
        name: "Bord & stolar",
        description: "Matbord, altanmöbler och kontorsstolar",
        image: "/table.webp",
        alt: "Matbord som lastas vid villaflytt i Svinninge, Österåker",
      },
      {
        name: "Vitvaror",
        description: "Tvättmaskin, kyl, frys och diskmaskin",
        image: "/washer.webp",
        alt: "Tvättmaskin som flyttas från radhus i Margretelund, Åkersberga",
      },
      {
        name: "Garderober & byråer",
        description: "Garderober, byråer och hyllor, isärtagna om det behövs",
        image: "/wardrobe.webp",
        alt: "Garderob som bärs ut vid flytt i Täljö, Österåker",
      },
      {
        name: "Flyttkartonger",
        description: "Kartonger från kök, garage, vind och förråd",
        image: "/box.webp",
        alt: "Flyttkartonger som lastas vid flytt i Österskär, Åkersberga",
      },
    ],
  },

  seoContent: {
    mainHeading: "Flytthjälp i Åkersberga och Österåker – villor, radhus och lägenheter",
    mainText:
      "Åkersberga är centralorten i Österåkers kommun, ungefär tre mil nordost om Stockholm. Bostäderna spänner från flerbostadshusen i Tråsättra och kring Åkersberga centrum till villaområden som Svinninge, Täljö och Österskär, där många hus ligger nära vattnet, och radhus och nya villor i Margretelund. Ute i kommunen finns också landsbygd och skärgård, med Ljusterö som största ö. För oss betyder det att ingen flytt i Österåker ser likadan ut: i lägenhetsområdena handlar det om trapphus, hiss och var bilen får stå, och i villaområdena om garage, förråd och en ibland lång väg från gatan.\n\nEn vanlig flytt hit går från en lägenhet i Stockholm eller Täby till ett hus i Åkersberga – familjer som vill ha mer plats men ändå kunna pendla. Vi kör Norrtäljevägen (E18) och svänger av vid Rosenkälla mot väg 276 in till Åkersberga. Sträckan från innerstaden är längre än de 20 km som ingår i paketpriset, så räkna med ett kilometertillägg; du ser totalen innan du bokar. Flyttar du i stället inom kommunen, till exempel från Tråsättra till ett radhus i Margretelund, är körningen kort och det är bärandet som tar tiden.\n\nRoslagsbanans gren mot Österskär stannar i Täljö, Åkers runö, Åkersberga, Tunagård och Österskär, och många bor nära en station men på gator där det är trångt att ställa en skåpbil. Vi frågar vid bokningen var bilen kan stå och planerar bärandet därefter. Ljusterö når man med den avgiftsfria vägfärjan från Östanå; ska du flytta dit, hör av dig innan du bokar så att vi kan stämma av restiden. Öar utan färja för bilar kan vi inte köra till.",
    preparationHeading: "Förbered flytten i Åkersberga",
    preparationText:
      "Packa i kartonger och märk med rum, töm byråer och garderober och frosta av frysen dagen innan. Ta ner lampor och gardinstänger, och lägg skruvar från det du tagit isär i märkta påsar. Gå igenom garaget, vinden och förrådet i god tid – det är där det brukar finnas mest.\n\nI villaområden som Svinninge och Österskär: håll uppfarten fri så att vi kan backa nära entrén, och berätta om vägen är smal eller saknar vändplats. I Tråsättra och kring centrum, ta reda på om bilen får köra fram till porten och om det finns hiss. Portkoder och nycklar till båda adresserna gör att vi kan börja direkt.",
    additionalSections: [
      {
        heading: "Områden vi flyttar i, i Åkersberga och Österåker",
        content:
          "Vi flyttar i hela Österåkers kommun där det går att köra: Åkersberga centrum, Tråsättra, Margretelund, Täljö, Svinninge, Österskär, Tunagård och Åkers runö med omnejd. Ljusterö tar vi efter överenskommelse, eftersom resan går med vägfärja.\n\nVi flyttar också mellan Österåker och grannkommunerna – Täby, Vallentuna, Vaxholm, Norrtälje och Stockholm. Körsträcka inom 20 km ingår i paketpriset och längre sträckor räknas per kilometer. Du ser hela priset innan du bokar.",
      },
      {
        heading: "Villaflytt i Österåker",
        content:
          "Villorna i Svinninge, Täljö och Österskär har ofta garage, förråd och trädgårdsmöbler som också ska med, och tomterna kan vara kuperade ner mot vattnet. Välj Extra Stor eller Mega Stor för en hel villa, så att det finns tid till allt. Det som inte ska med kan vi köra till återvinning samma dag om du bokar bortforsling.",
        cta: { text: "Se bortforsling i Stockholm →", href: "/bortforsling-stockholm" },
      },
      {
        heading: "Varför boka flytthjälp i Åkersberga med Ungdomskraft?",
        listItems: [
          {
            label: "Planering efter bostaden –",
            text: "Lägenhet i Tråsättra eller villa i Svinninge – vi anpassar paket och bärare efter var du bor och vart du ska.",
          },
          {
            label: "Pris med körsträcka –",
            text: "Paketpris och eventuellt kilometertillägg syns innan du bokar. Ingen offert och inget timpris.",
          },
          {
            label: "RUT-avdrag –",
            text: "Halva arbetskostnaden dras av på fakturan, upp till 75 000 kr per person och år.",
          },
          {
            label: "Försäkrat –",
            text: "Uppdragen är försäkrade genom Trygg Hansa.",
          },
          {
            label: "Alla dagar –",
            text: "Vi flyttar även helger, så du behöver inte ta ledigt.",
          },
        ],
      },
    ],
  },

  faq: [
    {
      question: "Vad kostar flytthjälp i Åkersberga?",
      answer:
        "Paketen kostar Liten 995 kr (1–2 föremål), Mellan 1 495 kr (3–6), Stor 2 495 kr (7–10), Extra Stor 4 995 kr (upp till 4 h) och Mega Stor 8 995 kr (upp till 8 h). 20 km körsträcka ingår; längre sträckor räknas per kilometer. Du ser totalen innan du bokar.",
    },
    {
      question: "Vad kostar det att flytta från Stockholm till Åkersberga?",
      answer:
        "Du betalar paketpriset plus kilometertillägg för körningen utöver 20 km. Beloppet räknas ut när du skriver in adresserna, innan du bokar.",
    },
    {
      question: "Kan jag få RUT-avdrag på flytthjälp i Österåker?",
      answer:
        "Ja, 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget görs direkt på fakturan.",
    },
    {
      question: "Flyttar ni till Ljusterö?",
      answer:
        "Ljusterö nås med den avgiftsfria bilfärjan från Östanå. Hör av dig innan du bokar en flytt dit, så stämmer vi av restiden.",
    },
    {
      question: "Är Åkersberga och Österåker samma sak?",
      answer:
        "Åkersberga är den största orten i Österåkers kommun. Vi flyttar i hela kommunen, inte bara i Åkersberga.",
    },
    {
      question: "Flyttar ni villor i Svinninge och Österskär?",
      answer:
        "Ja. Berätta om garage, förråd och hur långt det är från gatan till huset, så väljer vi paket och bärare därefter.",
    },
    {
      question: "Hur snabbt kan ni komma?",
      answer:
        "Ofta inom några dagar. Lediga tider för dina adresser syns i bokningen.",
    },
    {
      question: "Ingår skåpbil?",
      answer:
        "Ja, alla paket från Liten och uppåt har skåpbil och två bärare.",
    },
    {
      question: "Bär ni i trappor utan hiss?",
      answer:
        "Ja. Ange våning och om det finns hiss när du bokar.",
    },
    {
      question: "Flyttar ni vitvaror?",
      answer:
        "Ja, tvättmaskin, kyl, frys och diskmaskin. Frosta av och töm kyl och frys dagen innan.",
    },
    {
      question: "Tar ni isär och sätter ihop möbler?",
      answer:
        "Ja, sängar, garderober och bord som behöver tas isär sätter vi ihop igen på nya adressen.",
    },
    {
      question: "Kan jag flytta på helgen?",
      answer:
        "Ja, vi flyttar alla dagar i veckan.",
    },
    {
      question: "Vilka områden i Österåker täcker ni?",
      answer:
        "Åkersberga centrum, Tråsättra, Margretelund, Täljö, Svinninge, Österskär, Tunagård och Åkers runö, och Ljusterö efter överenskommelse.",
    },
    {
      question: "Vilka andra kommuner flyttar ni i?",
      answer: `Hela Stockholmsregionen: ${stockholmKommunerText}. Samma prismodell överallt – paketpris plus körsträcka över 20 km.`,
    },
  ],

  priceHint: PACKAGE_PRICE_HINT_SV.transport,

  pricingRows: getPackagePricingRows("transport", "sv"),

  dynamicHeadings: {
    threeSteps: "Flytthjälp i Åkersberga i 3 steg",
    step2Text:
      "Bärarna kommer med skåpbil, bär ut och lastar stadigt. Sedan kör vi till nya adressen i Österåker eller vidare i länet.",
    step3Title: "Allt på plats",
    step3Desc:
      "Vi bär in i rätt rum och sätter ihop det vi tog isär, så att du kan börja bo in dig direkt.",
    ctaHeading: "Boka flytthjälp i Åkersberga",
    ctaText:
      "Fyll i adresserna, välj paket och tid. Priset syns direkt, med bil och bärare.",
  },

  en: {
    slug: "moving-help-akersberga",
    meta: {
      title: "Moving Help Åkersberga – All of Österåker | Ungdomskraft",
      description:
        "Book moving help in Åkersberga and Österåker: Tråsättra, Täljö, Margretelund, Svinninge and Österskär. Van, carriers, fixed package price and RUT.",
      h1: "Moving Help Åkersberga",
    },
    hero: {
      heading: "Moving Help in Åkersberga",
      intro:
        "We move you in Åkersberga and the rest of Österåker Municipality – from the flat in Tråsättra or the centre to the villa in Svinninge, the terraced house in Margretelund or the house in Österskär. Carriers and a van come to the door, and you see the package price when you book.",
      heroImageAlt: "Ungdomskraft loading a van during a move in Åkersberga",
    },
    featureCards: {
      sinceText: "Moving help since 2014",
      storyText:
        "Ungdomskraft has moved households in Stockholm County since 2014. In Österåker we carry just as often from a flat near the Roslagsbanan as from a waterside villa in Svinninge, and we plan each move around what it looks like at the door.",
      bookYourText: "Book a move in Åkersberga",
      bookDescText:
        "Enter the old and new address, choose a package and a free time. The price is shown before you confirm.",
      vehicleTitleText: "Van and carriers from the E18 to your door",
      vehicleDescText:
        "We bring a van, blankets and straps and drive the whole way – no need to rent a vehicle or drive Norrtäljevägen yourself.",
    },
    itemsSection: {
      heading: "What we often move in Åkersberga",
      items: [
        { name: "Sofas & armchairs", description: "Corner sofas, modular sofas and armchairs", image: "/sofa.webp", alt: "Sofa carried during a move in Åkersberga centrum" },
        { name: "Beds & mattresses", description: "Double beds, children's beds and mattresses", image: "/bed.webp", alt: "Bed moved from a flat in Tråsättra, Åkersberga" },
        { name: "Tables & chairs", description: "Dining tables, patio furniture and office chairs", image: "/table.webp", alt: "Dining table loaded during a villa move in Svinninge, Österåker" },
        { name: "Appliances", description: "Washing machine, fridge, freezer and dishwasher", image: "/washer.webp", alt: "Washing machine moved from a terraced house in Margretelund, Åkersberga" },
        { name: "Wardrobes & dressers", description: "Wardrobes, dressers and shelves, taken apart if needed", image: "/wardrobe.webp", alt: "Wardrobe carried out during a move in Täljö, Österåker" },
        { name: "Moving boxes", description: "Boxes from the kitchen, garage, attic and storage", image: "/box.webp", alt: "Moving boxes loaded during a move in Österskär, Åkersberga" },
      ],
    },
    seoContent: {
      mainHeading: "Moving help in Åkersberga and Österåker – villas, terraced houses and flats",
      mainText:
        "Åkersberga is the main town of Österåker Municipality, about 30 km north-east of Stockholm. Homes range from the apartment blocks in Tråsättra and around Åkersberga centrum to villa areas like Svinninge, Täljö and Österskär, where many houses lie close to the water, and terraced houses and new villas in Margretelund. Further out the municipality has countryside and archipelago, with Ljusterö as its largest island. For us this means no two moves in Österåker look the same: in apartment areas it is about stairwells, lifts and where the van may stand, and in villa areas about garages, storage and a sometimes long walk from the street.\n\nA common move here goes from a flat in Stockholm or Täby to a house in Åkersberga – families who want more space but can still commute. We drive Norrtäljevägen (E18) and turn off at Rosenkälla onto road 276 into Åkersberga. The distance from central Stockholm is longer than the 20 km included in the package price, so expect a per-kilometre charge; you see the total before you book. If you are moving within the municipality instead, for example from Tråsättra to a terraced house in Margretelund, the drive is short and the carrying is what takes time.\n\nThe Roslagsbanan branch to Österskär stops at Täljö, Åkers runö, Åkersberga, Tunagård and Österskär, and many people live close to a station but on streets where it is tight to park a van. When you book we ask where the van can stand and plan the carrying from there. Ljusterö is reached by the free road ferry from Östanå; if you are moving there, get in touch before booking so we can confirm the travel time. Islands without a car ferry we cannot drive to.",
      preparationHeading: "Prepare your move in Åkersberga",
      preparationText:
        "Pack into boxes and label by room, empty dressers and wardrobes and defrost the freezer the day before. Take down lamps and curtain rods, and put screws from anything you've taken apart into labelled bags. Go through the garage, attic and storage in good time – that's usually where most things are.\n\nIn villa areas like Svinninge and Österskär, keep the driveway clear so we can reverse close to the entrance, and tell us if the road is narrow or has no turning area. In Tråsättra and around the centre, find out whether the van may drive up to the entrance and whether there is a lift. Door codes and keys for both addresses let us start straight away.",
      additionalSections: [
        {
          heading: "Areas we move in, in Åkersberga and Österåker",
          content:
            "We move across Österåker Municipality wherever you can drive: Åkersberga centrum, Tråsättra, Margretelund, Täljö, Svinninge, Österskär, Tunagård and Åkers runö and surroundings. Ljusterö we take by arrangement, since the trip is by road ferry.\n\nWe also move between Österåker and neighbouring municipalities – Täby, Vallentuna, Vaxholm, Norrtälje and Stockholm. Driving within 20 km is included in the package price and longer distances are charged per kilometre. You see the full price before you book.",
        },
        {
          heading: "Villa moves in Österåker",
          content:
            "Villas in Svinninge, Täljö and Österskär often have garages, storage and garden furniture that also need to come, and plots can slope down to the water. Choose Extra Large or Mega Large for a whole villa, so there is time for everything. Whatever isn't coming along we can take to recycling the same day if you book junk removal.",
          cta: { text: "See junk removal in Stockholm →", href: "/en/junk-removal-stockholm" },
        },
        {
          heading: "Why book moving help in Åkersberga with Ungdomskraft?",
          listItems: [
            { label: "Planned around your home –", text: "A flat in Tråsättra or a villa in Svinninge – we adjust package and carriers to where you live and where you are going." },
            { label: "Price including driving –", text: "Package price and any per-km charge show before you book. No quote and no hourly rate." },
            { label: "RUT deduction –", text: "Half the labour cost is deducted on the invoice, up to SEK 75,000 per person per year." },
            { label: "Insured –", text: "Jobs are insured through Trygg Hansa." },
            { label: "Every day –", text: "We move at weekends too, so you don't need time off." },
          ],
        },
      ],
    },
    faq: [
      { question: "How much does moving help in Åkersberga cost?", answer: "The packages cost Small SEK 995 (1–2 items), Medium SEK 1,495 (3–6), Large SEK 2,495 (7–10), Extra Large SEK 4,995 (up to 4 h) and Mega Large SEK 8,995 (up to 8 h). 20 km of driving is included; longer distances are charged per kilometre. You see the total before you book." },
      { question: "What does it cost to move from Stockholm to Åkersberga?", answer: "You pay the package price plus a per-km charge for driving beyond 20 km. The amount is calculated when you enter the addresses, before you book." },
      { question: "Can I get a RUT deduction on moving help in Österåker?", answer: "Yes, 50% of the labour cost, up to SEK 75,000 per person per year. It is applied directly on the invoice." },
      { question: "Do you move to Ljusterö?", answer: "Ljusterö is reached by the free car ferry from Östanå. Get in touch before booking a move there, and we'll confirm the travel time." },
      { question: "Are Åkersberga and Österåker the same?", answer: "Åkersberga is the largest town in Österåker Municipality. We move across the whole municipality, not only in Åkersberga." },
      { question: "Do you move villas in Svinninge and Österskär?", answer: "Yes. Tell us about the garage, storage and how far it is from the street to the house, and we'll choose package and carriers accordingly." },
      { question: "How soon can you come?", answer: "Often within a few days. Free times for your addresses show in the booking." },
      { question: "Is a van included?", answer: "Yes, every package from Small upwards has a van and two carriers." },
      { question: "Do you carry on stairs without a lift?", answer: "Yes. State the floor and whether there is a lift when booking." },
      { question: "Do you move appliances?", answer: "Yes – washing machine, fridge, freezer and dishwasher. Defrost and empty the fridge and freezer the day before." },
      { question: "Do you take apart and reassemble furniture?", answer: "Yes, beds, wardrobes and tables that need taking apart are put back together at the new address." },
      { question: "Can I move at the weekend?", answer: "Yes, we move every day of the week." },
      { question: "Which areas of Österåker do you cover?", answer: "Åkersberga centrum, Tråsättra, Margretelund, Täljö, Svinninge, Österskär, Tunagård and Åkers runö, and Ljusterö by arrangement." },
      { question: "Which other municipalities do you serve?", answer: `The whole Stockholm region: ${stockholmKommunerText}. Same pricing everywhere – package price plus driving beyond 20 km.` },
    ],
    priceHint: PACKAGE_PRICE_HINT_EN.transport,
    pricingRows: getPackagePricingRows("transport", "en"),
    dynamicHeadings: {
      threeSteps: "Moving help in Åkersberga in 3 steps",
      step2Text:
        "The carriers arrive with a van, carry out and load securely. Then we drive to the new address in Österåker or elsewhere in the county.",
      step3Title: "Everything in place",
      step3Desc:
        "We carry into the right rooms and reassemble what we took apart, so you can start settling in straight away.",
      ctaHeading: "Book moving help in Åkersberga",
      ctaText:
        "Enter the addresses, choose a package and time. The price shows straight away, with van and carriers.",
    },
  },
};
